import {sheetMachine, releaseDecision, dragOffset, velocityTracker, contentGesture, OWN_DRAG, DRAG} from './sheet-state.mjs';
import {t} from './i18n.mjs';
import {isWide} from './layout.mjs';
const IDS = ['filter', 'details'];  // the main menu is its own layer (drawer.mjs), not part of this one-at-a-time machine
const FOCUSABLE = 'button, input, select, a[href], summary';
// A control that can take focus now: not disabled, not inside a hidden page, not removed by CSS (the phone X of a sheet header is hidden on wide screens).
const usable = e => !e.disabled && !e.closest('[hidden]') && e.getClientRects?.().length !== 0;
// CSS uses the same breakpoint; narrower viewports retain bottom-sheet gestures.

// The same DOM preserves controls, scroll and disclosures across presentation changes.
export function sheets(onDetailClose, {reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches,
  desktop = () => isWide(),
  panel = desktop, onChange = () => {}} = {}) {
  const $ = id => document.getElementById(id);
  const machine = sheetMachine(), backdrop = $('backdrop');
  let previousFocus, token = 0;

  // Wait for the transform transition, with a timer in case transitionend never fires.
  function afterTransition(el, done) {
    if (reducedMotion()) { done(); return; }
    let finished = false;
    const finish = event => {
      if (finished || (event && (event.target !== el || !['transform', 'opacity'].includes(event.propertyName)))) return;
      finished = true; el.removeEventListener('transitionend', finish); clearTimeout(timer); done();
    };
    const timer = setTimeout(finish, 450);
    el.addEventListener('transitionend', finish);
  }
  // Panels (detail, filter) dock at the left edge from WIDE_MIN with no backdrop; below it they are modal bottom sheets.
  function presentation() {
    const id = machine.state === 'closed' ? null : machine.active;
    const wide = panel();
    document.documentElement?.toggleAttribute('data-panel-open', !!id && wide);
    document.documentElement?.toggleAttribute('data-sheet-open', !!id && !wide);
    for (const name of IDS) $(name).setAttribute('aria-modal', String(!wide));
    backdrop.hidden = !id || wide;
    syncPanelClose(id && wide ? id : null);
    onChange({id, panel: wide, element: id ? $(id) : null});
    document.dispatchEvent?.(new CustomEvent('sheetchange', {detail: {id}}));
  }
  // Right slot of the search bar: the X that closes the docked detail or filter panel (it names the panel it closes).
  function syncPanelClose(id) {
    const x = $('panel-close');
    if (!x) return;
    x.hidden = !id;
    if (id) { const key = id + 'Close'; x.setAttribute('data-i18n-aria', key); x.setAttribute('aria-label', t(key)); }
  }
  globalThis.addEventListener?.('resize', presentation);
  function setExpanded(id, value) {
    if (id === 'details') return;
    const button = $(id + '-button');
    button?.setAttribute('aria-expanded', String(value));
    button?.setAttribute('aria-pressed', String(value));
  }
  function hideNow(id) {
    const el = $(id); el.hidden = true; el.classList.remove('is-open', 'is-dragging'); el.style.transform = '';
    setExpanded(id, false); if (id === 'details') onDetailClose();
  }
  function openSheet(id) {
    const {changed, swap} = machine.open(id);
    if (!changed) return;
    if (swap) {
      if (!$(swap).contains?.(document.activeElement)) previousFocus = document.activeElement;
      hideNow(swap);
    } else previousFocus = document.activeElement;
    const el = $(id), mine = ++token;
    el.hidden = false; setExpanded(id, true); presentation();
    if (swap && panel()) el.classList.add('instant-swap');
    void el.offsetHeight;
    el.classList.add('is-open'); backdrop.classList.add('is-open');
    if (swap && panel()) { void el.offsetHeight; el.classList.remove('instant-swap'); }
    // The first control that is really there: hidden ones cannot take focus.
    // A sheet without a usable control (details while loading) takes focus itself, so Escape and Tab work from inside it.
    // The small X of a phone sheet header is not the start point: details focus the sheet itself, the filter its first control.
    ([...el.querySelectorAll(FOCUSABLE)].find(e => usable(e) && !e.classList?.contains('sheet-x')) ?? el).focus({preventScroll: true});
    afterTransition(el, () => { if (mine === token) machine.settled(id); });
  }
  function closeSheet() {
    const {changed, id} = machine.close();
    if (!changed) return;
    const el = $(id), mine = ++token;
    el.classList.remove('is-open', 'is-dragging'); el.style.transform = ''; backdrop.classList.remove('is-open');
    setExpanded(id, false);
    if (id === 'details') onDetailClose();
    afterTransition(el, () => {
      if (mine !== token) return;
      el.hidden = true; backdrop.hidden = true; machine.settled(id); presentation();
    });
    previousFocus?.focus?.({preventScroll: true});
  }
  // Shared drag: follows the finger, release decides by distance or flick speed.
  // Shared drag: follows the finger, release decides by distance or flick speed.
  function drag(el) {
    const tracker = velocityTracker();
    let origin = 0;
    return {
      start(y, time) { origin = y; tracker.reset(); tracker.add(y, time); },
      sample(y, time) { tracker.add(y, time); },
      move(y, time) {
        tracker.add(y, time); const dy = y - origin;
        el.classList.add('is-dragging');
        el.style.transform = `translateY(${dragOffset(dy)}px)`;
        backdrop.style.opacity = String(Math.max(0, 1 - Math.max(0, dy) / el.offsetHeight));
      },
      end(y, cancelled) {
        backdrop.style.opacity = ''; el.classList.remove('is-dragging');
        if (!cancelled && releaseDecision(y - origin, tracker.velocity(), el.offsetHeight) === 'close') closeSheet();
        else el.style.transform = '';
      },
    };
  }
  function draggable(id) {
    const el = $(id), header = el.querySelector('.sheet-header'), body = el.querySelector('.sheet-body'), d = drag(el);
    // Header: pointer events for mouse, pen and touch (touch-action: none in CSS).
    let start = null, dragging = false, pointer = null;
    header.addEventListener('pointerdown', event => {
      if (event.button > 0 || event.target.closest('button, a') || desktop()) return;
      start = event.clientY; pointer = event.pointerId; dragging = false; d.start(event.clientY, event.timeStamp);
      header.setPointerCapture?.(pointer);
    });
    header.addEventListener('pointermove', event => {
      if (start === null || event.pointerId !== pointer) return;
      if (!dragging && Math.abs(event.clientY - start) < DRAG.slop) { d.sample(event.clientY, event.timeStamp); return; }
      dragging = true; d.move(event.clientY, event.timeStamp);
    });
    const release = event => {
      if (start === null || event.pointerId !== pointer) return;
      const was = dragging; start = null; dragging = false; header.releasePointerCapture?.(pointer);
      if (was) d.end(event.clientY, event.type !== 'pointerup');
    };
    header.addEventListener('pointerup', release);
    header.addEventListener('pointercancel', release);
    if (!body) return;
    // Content: touch events, because the browser would take over a pointer gesture for scrolling.
    // touchmove must be non-passive so the first move can be claimed with preventDefault.
    let touch = null;
    body.addEventListener('touchstart', event => {
      const t = event.touches[0];
      touch = event.touches.length === 1 && !desktop() && !event.target.closest?.(OWN_DRAG)
        ? {x: t.clientX, y: t.clientY, mode: 'pending', scrollTop: body.scrollTop, scrollable: body.scrollHeight > body.clientHeight + 1} : null;
    }, {passive: true});
    body.addEventListener('touchmove', event => {
      if (!touch || touch.mode === 'scroll') return;
      const t = event.touches[0];
      if (touch.mode === 'pending') {
        const mode = contentGesture({dy: t.clientY - touch.y, dx: t.clientX - touch.x, scrollTop: Math.min(touch.scrollTop, body.scrollTop), scrollable: touch.scrollable});
        if (mode === 'pending') return;
        // Once native scrolling runs, moves are no longer cancelable; then leave it to the browser.
        if (mode === 'scroll' || event.cancelable === false) { touch.mode = 'scroll'; return; }
        touch.mode = 'sheet'; d.start(t.clientY, event.timeStamp);
      }
      event.preventDefault();
      d.move(t.clientY, event.timeStamp);
    }, {passive: false});
    const finish = event => {
      if (touch?.mode === 'sheet') d.end(event.changedTouches[0].clientY, event.type === 'touchcancel');
      touch = null;
    };
    body.addEventListener('touchend', finish);
    body.addEventListener('touchcancel', finish);
  }
  backdrop.onclick = closeSheet;
  document.addEventListener('keydown', event => {
    const id = machine.active;
    // The menu drawer lies on top of everything and takes Escape and Tab itself (drawer.mjs).
    if (!id || machine.state === 'closing' || document.documentElement?.hasAttribute?.('data-drawer-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSheet();
    }
    if (event.key === 'Tab' && !panel()) {
      const items = [...$(id).querySelectorAll(FOCUSABLE)].filter(usable);
      const first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
  for (const id of IDS) { draggable(id); const close = $(id + '-close'); if (close) close.onclick = closeSheet; }
  const panelClose = $('panel-close'); if (panelClose) panelClose.onclick = closeSheet;
  for (const id of IDS) setExpanded(id, false);
  presentation();
  function toggleSheet(id) {
    if (machine.active === id && ['open', 'opening'].includes(machine.state)) closeSheet();
    else openSheet(id);
  }
  return {openSheet, closeSheet, toggleSheet, get active() { return machine.state === 'closed' ? null : machine.active; }};
}
